import { useLayoutEffect, useRef, useState } from 'react';

import { percentOf } from '@core/money';

import type { Category } from '@/lib/types';

import { CategoryBadge, useCategoryColors } from './ui';

export type Bar = { key: string; value: number; label: string; tooltip: string };

/** Minimal bar chart: one gridline, sparse labels, hover/tap for exact values. */
export function BarChart({
  data,
  format,
  formatAxis = format,
  height = 180,
  highlight,
  onSelect,
  label,
}: {
  data: Bar[];
  format: (v: number) => string;
  formatAxis?: (v: number) => string;
  height?: number;
  highlight?: string;
  onSelect?: (bar: Bar) => void;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const max = Math.max(0, ...data.map((d) => d.value));
  const plot = height - 22;
  const slot = data.length ? width / data.length : 0;
  const bw = Math.max(3, Math.min(30, slot * 0.6));
  const every = Math.max(1, Math.ceil(data.length / 8));
  const active = hover !== null ? data[hover] : null;

  return (
    <div>
      <div style={{ minHeight: 28, marginBottom: 6 }} aria-live="polite">
        {active ? (
          <span>
            <span className="muted">{active.tooltip}</span> · <strong className="num">{format(active.value)}</strong>
          </span>
        ) : (
          <span className="faint" style={{ fontSize: 13 }}>
            {max > 0 ? `Hover a bar for details · max ${formatAxis(max)}` : 'No spending in this period'}
          </span>
        )}
      </div>
      <div ref={ref} role="img" aria-label={label} style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} onMouseLeave={() => setHover(null)}>
            <line x1={0} x2={width} y1={plot} y2={plot} stroke="var(--border)" />
            {max > 0 && <line x1={0} x2={width} y1={1} y2={1} stroke="var(--border)" strokeDasharray="3 4" />}
            {data.map((d, i) => {
              const h = max > 0 ? Math.max(d.value > 0 ? 3 : 0, (d.value / max) * (plot - 4)) : 0;
              const on = hover === i || (hover === null && d.key === highlight);
              return (
                <g key={d.key}>
                  <rect
                    x={i * slot + (slot - bw) / 2}
                    y={plot - h}
                    width={bw}
                    height={h}
                    rx={Math.min(4, bw / 2)}
                    fill={on ? 'var(--primary)' : hover !== null ? 'var(--surface-muted)' : 'var(--bar)'}
                  />
                  <rect
                    x={i * slot}
                    y={0}
                    width={slot}
                    height={plot}
                    fill="transparent"
                    style={{ cursor: onSelect && d.value > 0 ? 'pointer' : 'default' }}
                    onMouseEnter={() => setHover(i)}
                    onClick={() => onSelect && d.value > 0 && onSelect(d)}
                  />
                  {(i % every === 0 || i === data.length - 1) && (
                    <text x={i * slot + slot / 2} y={height - 4} textAnchor="middle" fontSize={11} fill="var(--text-3)">
                      {d.label}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
}

export type CategoryBar = { category: Category; value: number };

export function CategoryBars({
  items,
  format,
  onSelect,
  total: totalProp,
}: {
  items: CategoryBar[];
  /** Overall total when `items` is only the top few, so percentages stay true. */
  total?: number;
  format: (v: number) => string;
  onSelect?: (c: Category) => void;
}) {
  const total = totalProp ?? items.reduce((s, i) => s + i.value, 0);
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="bars">
      {items.map((it) => (
        <CategoryRow key={it.category.id} item={it} pct={percentOf(it.value, total)} width={(it.value / max) * 100} format={format} onSelect={onSelect} />
      ))}
    </div>
  );
}

function CategoryRow({
  item,
  pct,
  width,
  format,
  onSelect,
}: {
  item: CategoryBar;
  pct: number;
  width: number;
  format: (v: number) => string;
  onSelect?: (c: Category) => void;
}) {
  const colors = useCategoryColors(item.category.color);
  return (
    <button className="list-row" style={{ padding: '4px 6px' }} onClick={() => onSelect?.(item.category)} disabled={!onSelect}>
      <CategoryBadge icon={item.category.icon} color={item.category.color} />
      <div className="grow">
        <div className="row spread">
          <strong className="ellipsis">{item.category.name}</strong>
          <span className="row" style={{ gap: 12 }}>
            <strong className="num">{format(item.value)}</strong>
            <span className="muted num" style={{ width: 38, textAlign: 'right' }}>
              {pct}%
            </span>
          </span>
        </div>
        <div className="bar-track">
          <div className="bar-fill" style={{ width: `${Math.max(2, width)}%`, background: colors.fg }} />
        </div>
      </div>
    </button>
  );
}
